import React, { useState, useEffect } from 'react';
import { crmAPI } from '../../lib/api';
import { Plus, Edit, Trash2, Search, RefreshCw, Eye } from 'lucide-react';

const Clients = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingClient, setViewingClient] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', address: '', contact_person: '', thana: '', district: '', country: '', is_active: true
    });

    useEffect(() => { fetchClients(); }, []);

    const fetchClients = async () => {
        try {
            setLoading(true);
            const response = await crmAPI.getClients();
            setClients(response.data.data || response.data);
        } catch (err) { setError('Failed to load data'); }
        finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) await crmAPI.updateClient(editingId, formData);
            else await crmAPI.createClient(formData);
            setShowModal(false);
            resetForm();
            fetchClients();
        } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    };

    const handleEdit = (client) => {
        setFormData({ name: client.name, email: client.email || '', phone: client.phone || '', address: client.address || '', contact_person: client.contact_person || '', thana: client.thana || '', district: client.district || '', country: client.country || '', is_active: client.is_active !== false });
        setEditingId(client.id);
        setShowModal(true);
    };

    const handleView = (client) => {
        setViewingClient(client);
        setShowViewModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            await crmAPI.deleteClient(id);
            fetchClients();
        } catch (err) { setError('Failed to delete'); }
    };

    const resetForm = () => { setFormData({ name: '', email: '', phone: '', address: '', contact_person: '', thana: '', district: '', country: '', is_active: true }); setEditingId(null); };
    const filteredClients = clients.filter(c => c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || c.email?.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Clients</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Manage clients</p>
                </div>
                <button onClick={() => { resetForm(); setShowModal(true); }} className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                    <Plus className="w-4 h-4 mr-2" /> Add Client
                </button>
            </div>
            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}
            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" />
            </div>
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Phone</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Contact Person</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {filteredClients.length > 0 ? filteredClients.map(client => (
                                    <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200 font-medium">{client.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{client.email || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{client.phone || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{client.contact_person || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <button onClick={() => handleView(client)} className="text-blue-600 hover:text-blue-900 mr-3"><Eye className="w-4 h-4" /></button>
                                            <button onClick={() => handleEdit(client)} className="text-indigo-600 hover:text-indigo-900 mr-3"><Edit className="w-4 h-4" /></button>
                                            <button onClick={() => handleDelete(client.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No clients found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">{editingId ? 'Edit' : 'Add'} Client</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Client Name</label>
                                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                                <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                                <input type="text" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Person</label>
                                <input type="text" value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                            </div>
                            {/* address */}
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
                                <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Upzila/Thana/Area</label>
                                <input type="text" value={formData.thana} onChange={(e) => setFormData({...formData, thana: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">District</label>
                                <input type="text" value={formData.district} onChange={(e) => setFormData({...formData, district: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
                                <input type="text" value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                            </div>
                            <div className="mb-3 flex items-center">
                                <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="mr-2" />
                                <label className="text-sm text-gray-700 dark:text-gray-300">Active</label>
                            </div>
                            <div className="flex justify-end gap-3 mt-4">
                                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="px-4 py-2 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">{editingId ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {showViewModal && viewingClient && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg w-full max-w-lg mx-4 overflow-hidden">
                        <div className="bg-indigo-600 px-6 py-4">
                            <h2 className="text-xl font-semibold text-white">Client Details</h2>
                            <p className="text-indigo-100 text-sm mt-1">View client information</p>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-slate-600 pb-3">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Client ID</span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{viewingClient.unique_id || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-slate-600 pb-3">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Client Name</span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{viewingClient.name || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-slate-600 pb-3">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Email</span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{viewingClient.email || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-slate-600 pb-3">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Phone</span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{viewingClient.phone || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-slate-600 pb-3">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Contact Person</span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{viewingClient.contact_person || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-slate-600 pb-3">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Address</span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{viewingClient.address || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-slate-600 pb-3">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Upzila/Thana/Area</span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{viewingClient.thana || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-slate-600 pb-3">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">District</span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{viewingClient.district || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-slate-600 pb-3">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Country</span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{viewingClient.country || '-'}</span>
                                </div>
                                <div className="flex justify-between items-center border-b border-gray-200 dark:border-slate-600 pb-3">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                                    <span className={`px-2 py-1 text-xs rounded-full ${viewingClient.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {viewingClient.is_active !== false ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                            <div className="flex justify-end mt-6">
                                <button onClick={() => { setShowViewModal(false); setViewingClient(null); }} className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg">Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Clients;
